/**
 * 올인원 — 알림톡 탭
 * docs/tools_all.md §10
 *
 * - 10-1 식별 회원 → resendQrAlimtalk
 * - 10-3 탭 내부 발송 이력 (localStorage 백업, 동일 회원 재발송 Popconfirm)
 * - 신규가입(미등록 → registerMemberOnly)은 좌측 패널에서 처리, 결과 회원이
 *   useMemberIdentity 에 set 되면 본 탭이 자동으로 카드 표시
 */

import React, { useEffect, useState } from 'react';
import {
  Card, Button, Descriptions, Empty, Popconfirm, Space, Tag, Typography,
  message as messageApi,
} from 'antd';
import { MessageOutlined } from '@ant-design/icons';
import { resendQrAlimtalk } from '../../services/api';

const { Text } = Typography;

const MAX_HISTORY = 50;

const getDisplayName = (m) => m?.nickname || m?.name || (m?.member_type === 'CHILD' ? '자녀' : '회원');

export default function AlimtalkTab({
  scannedMember, beginAction, endAction, onLog,
}) {
  const [history, setHistory] = useState([]);
  const [sending, setSending] = useState(false);

  // 식별 회원이 바뀌거나 해제되면 발송이력 초기화 — 올인원 '이전 회원 기록 비노출' 정책 정합.
  // (이전엔 sessionStorage 에 세션 전체가 누적돼 다른 회원·이전 진입 발송까지 '전체'로 노출됐음)
  useEffect(() => { setHistory([]); }, [scannedMember?.member_id]);

  if (!scannedMember) {
    return (
      <Empty
        description={
          <>
            회원을 식별하거나 좌측에서 미등록 전화 입력 후 가입을 진행하세요.
          </>
        }
      />
    );
  }

  const alreadySentInSession = history.some((h) => h.member_id === scannedMember.member_id);

  const doSend = async () => {
    const actionId = `alim-${scannedMember.member_id}-${Date.now()}`;
    setSending(true);
    beginAction(actionId);
    try {
      const res = await resendQrAlimtalk(scannedMember.member_id);
      if (res?.success === false) throw new Error(res.message || '발송 실패');
      const entry = {
        member_id: scannedMember.member_id,
        name: getDisplayName(scannedMember),
        phone: scannedMember.phone || scannedMember.phone_masked || '',
        ts: new Date().toISOString(),
      };
      setHistory((prev) => [entry, ...prev].slice(0, MAX_HISTORY));
      messageApi.success('알림톡 발송 완료');
      onLog?.(`✅ 알림톡 발송: ${entry.name}`);
    } catch (err) {
      messageApi.error(err?.message || '발송 실패');
      onLog?.(`❌ 알림톡 실패: ${err?.message || ''}`);
    } finally {
      endAction(actionId);
      setSending(false);
    }
  };

  // 이미 발송한 회원이면 onClick 을 제거 — Popconfirm 의 onConfirm 만 발송 경로로 둔다.
  // (Popconfirm 은 자식 onClick 을 막지 않으므로 onClick 을 두면 확인 전에 즉시 발송됨)
  const SendButton = (
    <Button
      type="primary" size="large" icon={<MessageOutlined />}
      loading={sending} onClick={alreadySentInSession ? undefined : doSend} block
    >
      💬 알림톡 발송
    </Button>
  );

  return (
    <div className="alimtalk-tab">
      <Card size="small" title={`${getDisplayName(scannedMember)} — 알림톡`} style={{ marginBottom: 16 }}>
        <Descriptions size="small" column={1} bordered>
          <Descriptions.Item label="회원 ID">{scannedMember.member_id}</Descriptions.Item>
          <Descriptions.Item label="전화">{scannedMember.phone || scannedMember.phone_masked || '-'}</Descriptions.Item>
          <Descriptions.Item label="가입일">{scannedMember.created_at?.slice(0, 10) || '-'}</Descriptions.Item>
          <Descriptions.Item label="총 방문">{scannedMember.total_visits ?? scannedMember.visit_count ?? '-'}</Descriptions.Item>
          <Descriptions.Item label="최근 방문">{scannedMember.last_visit_at?.slice(0, 10) || '-'}</Descriptions.Item>
        </Descriptions>

        <div style={{ marginTop: 12 }}>
          {alreadySentInSession ? (
            <Popconfirm
              title="이 회원에게 이미 발송함"
              description="다시 발송할까요?"
              okText="재발송" cancelText="취소"
              onConfirm={doSend}
            >
              {SendButton}
            </Popconfirm>
          ) : SendButton}
        </div>
      </Card>

      <Card size="small" title="이 회원 발송 이력">
        {history.length === 0 ? (
          <Text type="secondary">이 회원에게 보낸 이력이 없습니다.</Text>
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size={4}>
            {history.map((h, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span>
                  <Tag color="blue">{h.ts.slice(11, 16)}</Tag>
                  {h.name}
                </span>
                <Text type="secondary" style={{ fontSize: 12 }}>{h.phone}</Text>
              </div>
            ))}
          </Space>
        )}
      </Card>
    </div>
  );
}
